import {
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import React from "react";
import { IMobileRenderTableV2Props } from "./RenderMobileTableV2.types";
import { SelectAll } from "../../../Table/components/SelectAll/SelectAll";
import { MobileRowDetailDialog } from "./MobileRowDetailDialog";

export const CustomMobileTableV2: React.FC<IMobileRenderTableV2Props> = (
  props,
) => {
  const {
    rows,
    mobileColumns,
    masterColumns,
    isDataLoading,
    loaderSkeletonRows,
    totalRecords,
    uniqueRowKeyName,
    isPagination,
    rowsPerPageOptions,
    onRowsPerPageChange,
    selectType,
    selectionMode,
    selectedRows,
    pageNumber = 1,
    onPageChange,
    onSelectionAllRows,
    onSelectRow,
    rowHighlightCondition,
    onRowClick,
    onSeeAllDetails,
    onCopyPhone,
    onWhatsappClick,
  } = props;

  const [pageState, setPageState] = React.useState(pageNumber - 1);
  const [rowsPerPageState, setRowsPerPageState] =
    React.useState(rowsPerPageOptions);
  const [selectedDialogRow, setSelectedDialogRow] = React.useState<any>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setPageState(pageNumber - 1);
  }, [pageNumber]);

  React.useEffect(() => {
    setRowsPerPageState(rowsPerPageOptions);
  }, [rowsPerPageOptions]);

  const activePage = pageNumber ? pageNumber - 1 : pageState;
  const activeRowsPerPage = rowsPerPageOptions || rowsPerPageState || 5;

  const isServerSide =
    totalRecords !== undefined && totalRecords !== null && totalRecords > 0;

  const currentPageItems = React.useMemo(() => {
    if (props.extraFetchFactor) {
      return rows.slice(0, activeRowsPerPage);
    }
    if (isServerSide) {
      return rows;
    }
    if (activeRowsPerPage > 0 && isPagination) {
      return rows.slice(
        activePage * activeRowsPerPage,
        activePage * activeRowsPerPage + activeRowsPerPage,
      );
    }
    return rows;
  }, [
    rows,
    props.extraFetchFactor,
    isServerSide,
    activeRowsPerPage,
    isPagination,
    activePage,
  ]);

  const totalPages = isServerSide
    ? Math.ceil(totalRecords / (activeRowsPerPage || 1))
    : Math.ceil(rows.length / (activeRowsPerPage || 1));

  const hasNextPage = props.extraFetchFactor
    ? rows.length > activeRowsPerPage
    : activePage < totalPages - 1;

  const finalTotal = isServerSide ? totalRecords : rows.length;

  const actualShownCount = props.extraFetchFactor
    ? Math.min(rows.length, activeRowsPerPage)
    : Math.min((activePage + 1) * activeRowsPerPage, finalTotal) -
      activePage * activeRowsPerPage;

  const from =
    finalTotal === 0 && rows.length === 0
      ? 0
      : activePage * activeRowsPerPage + 1;
  const to = props.extraFetchFactor
    ? activePage * activeRowsPerPage + actualShownCount
    : Math.min((activePage + 1) * activeRowsPerPage, finalTotal);

  const handlePageChange = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
    setPageState(newPage);
  };

  const handleItemClick = (row: any) => {
    setSelectedDialogRow(row);
    setDialogOpen(true);
    if (onRowClick) {
      onRowClick(row);
    }
  };

  if (isDataLoading) {
    return <CardSkeleton loaderSkeletonRows={loaderSkeletonRows} />;
  }

  return (
    <>
      {selectionMode === "multiple" && (
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
            p: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SelectAll
                checkedType={selectType}
                onChange={onSelectionAllRows}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Select All
              </Typography>
            </Box>
            {(selectedRows.length > 0 || selectType === "AllPages") && (
              <Typography variant="body2">
                {`(${
                  selectType === "AllPages" ? totalRecords : selectedRows.length
                } Rows Selected)`}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* PhonePe History Style Seamless Full-Width Edge-to-Edge List */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#ffffff",
          mb: 1.5,
        }}
      >
        {currentPageItems.map((row, index) => {
          const firstCol =
            mobileColumns && mobileColumns.length > 0
              ? mobileColumns[0]
              : undefined;

          // 1. Heading Name (Line 2 Title)
          let name: React.ReactNode = "";
          if (firstCol?.headingColumn?.onRowCellRender) {
            name = firstCol.headingColumn.onRowCellRender(
              {
                value: row[firstCol.headingColumn.fieldName],
                rowTextTurncateLength: undefined,
                column: {
                  key: firstCol.headingColumn.key,
                  headerName: "",
                  fieldName: firstCol.headingColumn.fieldName,
                  columnIndex: 0,
                  rowIndex: index,
                },
              },
              row,
            );
          } else if (firstCol?.headingColumn?.onRowCellValueRender) {
            name = firstCol.headingColumn.onRowCellValueRender(
              row[firstCol.headingColumn.fieldName],
              row,
            );
          } else {
            name =
              (firstCol?.headingColumn?.fieldName
                ? row[firstCol.headingColumn.fieldName]
                : "") ||
              row.name_of_guest ||
              row.name ||
              row.title ||
              row.customer_name ||
              row.event_name ||
              row.booking_name ||
              row.party_name ||
              row.client_name ||
              row.full_name ||
              row.employee_name ||
              (masterColumns && masterColumns.length > 0
                ? row[masterColumns[0].fieldName]
                : null) ||
              "Record";
          }

          // 2. Right Heading (Line 2 Status/Badge)
          let rightHeading: React.ReactNode = null;
          if (firstCol?.rightHeadingColumn?.onRowCellRender) {
            rightHeading = firstCol.rightHeadingColumn.onRowCellRender(
              {
                value: row[firstCol.rightHeadingColumn.fieldName],
                rowTextTurncateLength: undefined,
                column: {
                  key: firstCol.rightHeadingColumn.key,
                  headerName: "",
                  fieldName: firstCol.rightHeadingColumn.fieldName,
                  columnIndex: 0,
                  rowIndex: index,
                },
              },
              row,
            );
          } else if (firstCol?.rightHeadingColumn?.onRowCellValueRender) {
            rightHeading = firstCol.rightHeadingColumn.onRowCellValueRender(
              row[firstCol.rightHeadingColumn.fieldName],
              row,
            );
          } else if (
            row.status ||
            row.actual_status ||
            row.state ||
            row.stage
          ) {
            const statusVal =
              row.status || row.actual_status || row.state || row.stage;
            const sUpper = String(statusVal).toUpperCase();
            rightHeading = (
              <Chip
                label={sUpper}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor:
                    sUpper === "BOOKED" ||
                    sUpper === "COMPLETED" ||
                    sUpper === "APPROVED"
                      ? "#dcfce7"
                      : sUpper === "CANCELLED" ||
                          sUpper === "DEAD" ||
                          sUpper === "REJECTED"
                        ? "#fee2e2"
                        : "#e0f2fe",
                  color:
                    sUpper === "BOOKED" ||
                    sUpper === "COMPLETED" ||
                    sUpper === "APPROVED"
                      ? "#15803d"
                      : sUpper === "CANCELLED" ||
                          sUpper === "DEAD" ||
                          sUpper === "REJECTED"
                        ? "#b91c1c"
                        : "#0369a1",
                  borderRadius: "6px",
                }}
              />
            );
          }

          // 3. Top Banner Left & Right
          let topBannerLeft: React.ReactNode = null;
          if (firstCol?.topBanner?.leftColumn?.onRowCellRender) {
            topBannerLeft = firstCol.topBanner.leftColumn.onRowCellRender(
              {
                value: row[firstCol.topBanner.leftColumn.fieldName],
                rowTextTurncateLength: undefined,
                column: {
                  key: firstCol.topBanner.leftColumn.key,
                  headerName: "",
                  fieldName: firstCol.topBanner.leftColumn.fieldName,
                  columnIndex: 0,
                  rowIndex: index,
                },
              },
              row,
            );
          } else if (firstCol?.topBanner?.leftColumn?.onRowCellValueRender) {
            topBannerLeft = firstCol.topBanner.leftColumn.onRowCellValueRender(
              row[firstCol.topBanner.leftColumn.fieldName],
              row,
            );
          } else if (row.preferred_venue || row.venue || row.category) {
            topBannerLeft = (
              <Chip
                label={row.preferred_venue || row.venue || row.category}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.685rem",
                  fontWeight: 700,
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  borderRadius: "6px",
                  maxWidth: "60%",
                }}
              />
            );
          }

          let topBannerRight: React.ReactNode = null;
          if (firstCol?.topBanner?.rightColumn?.onRowCellRender) {
            topBannerRight = firstCol.topBanner.rightColumn.onRowCellRender(
              {
                value: row[firstCol.topBanner.rightColumn.fieldName],
                rowTextTurncateLength: undefined,
                column: {
                  key: firstCol.topBanner.rightColumn.key,
                  headerName: "",
                  fieldName: firstCol.topBanner.rightColumn.fieldName,
                  columnIndex: 0,
                  rowIndex: index,
                },
              },
              row,
            );
          } else if (firstCol?.topBanner?.rightColumn?.onRowCellValueRender) {
            topBannerRight =
              firstCol.topBanner.rightColumn.onRowCellValueRender(
                row[firstCol.topBanner.rightColumn.fieldName],
                row,
              );
          }

          // 4. SubSection Columns (Array of row cells)
          const subSections = firstCol?.subSectionColumn || [];

          const isSelected =
            selectedRows.findIndex(
              (x) => x[uniqueRowKeyName] === row[uniqueRowKeyName],
            ) > -1 || selectType === "AllPages";

          return (
            <Box
              key={row[uniqueRowKeyName] || index}
              onClick={() => handleItemClick(row)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                py: 1.5,
                px: { xs: 1.5, sm: 2 },
                borderBottom: "1px solid #f1f5f9",
                backgroundColor: rowHighlightCondition?.(row) || "#ffffff",
                cursor: "pointer",
                transition: "background-color 0.15s ease-in-out",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
                "&:active": {
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              {selectionMode !== "none" && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectRow(row)(e, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ mr: 1, p: 0.25, mt: 0.5 }}
                />
              )}

              {/* Content Container */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.4,
                }}
              >
                {/* Line 1: Top Banner (Left & Right) */}
                {(topBannerLeft || topBannerRight) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box>{topBannerLeft}</Box>
                    {topBannerRight && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {topBannerRight}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Line 2: Heading / Name (Left) & Right Heading / Status + Chevron (Right) */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  {typeof name === "string" ? (
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        fontSize: "0.975rem",
                        color: "#0f172a",
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                      }}
                    >
                      {name}
                    </Typography>
                  ) : (
                    name
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {rightHeading}
                    <KeyboardArrowRight
                      sx={{ color: "#cbd5e1", fontSize: "18px" }}
                    />
                  </Box>
                </Box>

                {/* Line 3+: SubSection Columns defined by parent view or generic fallbacks */}
                {subSections.length > 0 ? (
                  subSections.map((subCol, sIdx) => {
                    let cellContent: React.ReactNode = null;
                    if (subCol.onRowCellRender) {
                      cellContent = subCol.onRowCellRender(
                        {
                          value: row[subCol.fieldName],
                          rowTextTurncateLength: undefined,
                          column: {
                            key: subCol.key,
                            headerName: "",
                            fieldName: subCol.fieldName,
                            columnIndex: sIdx,
                            rowIndex: index,
                          },
                        },
                        row,
                      );
                    } else if (subCol.onRowCellValueRender) {
                      cellContent = subCol.onRowCellValueRender(
                        row[subCol.fieldName],
                        row,
                      );
                    } else if (
                      row[subCol.fieldName] !== undefined &&
                      row[subCol.fieldName] !== null
                    ) {
                      cellContent = (
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748b", fontSize: "0.75rem" }}
                        >
                          {String(row[subCol.fieldName])}
                        </Typography>
                      );
                    }

                    return (
                      <Box key={subCol.key || sIdx} sx={{ width: "100%" }}>
                        {cellContent}
                      </Box>
                    );
                  })
                ) : (
                  /* Generic Fallback SubSections from masterColumns or row fields */
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {row.contact_number_one ||
                    row.phone ||
                    row.mobile ||
                    row.contact_number ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#475569",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {row.contact_number_one ||
                          row.phone ||
                          row.mobile ||
                          row.contact_number}
                      </Typography>
                    ) : masterColumns && masterColumns.length > 1 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontSize: "0.75rem",
                        }}
                      >
                        {String(row[masterColumns[1].fieldName] || "")}
                      </Typography>
                    ) : null}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Pagination - Mobile Page Controls */}
      {isPagination && (
        <Box
          sx={{
            mt: 1,
            mb: 2,
            px: 1.5,
            py: 1.25,
            backgroundColor: "#ffffff",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            {/* Navigation Buttons: Previous & Next Page */}
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              {activePage > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<KeyboardArrowLeft />}
                  onClick={(e) => handlePageChange(e, activePage - 1)}
                  sx={{
                    borderRadius: "20px",
                    py: 0.85,
                    fontWeight: 600,
                    fontSize: "0.825rem",
                    textTransform: "none",
                  }}
                >
                  Previous Page
                </Button>
              )}
              {hasNextPage && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  endIcon={<KeyboardArrowRight />}
                  onClick={(e) => handlePageChange(e, activePage + 1)}
                  sx={{
                    borderRadius: "20px",
                    py: 0.85,
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    boxShadow: "none",
                    textTransform: "none",
                  }}
                >
                  {props.extraFetchFactor && !isServerSide
                    ? "Next Page"
                    : `Next Page (${activePage + 2} of ${totalPages})`}
                </Button>
              )}
            </Stack>

            {/* Rows info & selector */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                {props.extraFetchFactor && !isServerSide ? (
                  <>
                    Showing{" "}
                    <b>
                      {from}-{to}
                    </b>
                  </>
                ) : (
                  <>
                    Showing{" "}
                    <b>
                      {from}-{to}
                    </b>{" "}
                    of <b>{finalTotal}</b>
                  </>
                )}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontWeight: 600 }}
                >
                  {props.extraFetchFactor && !isServerSide
                    ? `Page ${activePage + 1}`
                    : `Page ${activePage + 1}/${totalPages || 1}`}
                </Typography>
                <Select
                  value={activeRowsPerPage}
                  onChange={(e) => {
                    const val = parseInt(e.target.value as string, 10);
                    const limit = val === -1 ? finalTotal : val;
                    if (onRowsPerPageChange) {
                      onRowsPerPageChange(1, limit);
                    }
                    if (onPageChange) {
                      onPageChange(1);
                    }
                    setRowsPerPageState(val);
                    setPageState(0);
                  }}
                  size="small"
                  sx={{
                    height: "30px",
                    fontSize: "0.775rem",
                    fontWeight: 600,
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  {(props.paginationList || [5, 10, 25, 50, 100]).map(
                    (option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {option} rows
                      </MenuItem>
                    ),
                  )}
                </Select>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Popout Row Detail Dialog */}
      <MobileRowDetailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        row={selectedDialogRow}
        mobileColumns={mobileColumns}
        masterColumns={masterColumns}
        onSeeAllDetails={onSeeAllDetails}
        onCopyPhone={onCopyPhone}
        onWhatsappClick={onWhatsappClick}
        uniqueRowKeyName={uniqueRowKeyName}
      />
    </>
  );
};

export const CardSkeleton: React.FC<{ loaderSkeletonRows: number }> = (
  props,
) => {
  const { loaderSkeletonRows = 4 } = props;
  const rows = Array.from(
    Array(loaderSkeletonRows),
    (_, i) => `skeleton-row-${i + 1}`,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "#ffffff",
      }}
    >
      {rows.map((row) => (
        <Box
          key={row}
          sx={{
            py: 1.6,
            px: 2,
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Skeleton variant="circular" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={22} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Skeleton
            variant="rounded"
            width={65}
            height={24}
            sx={{ borderRadius: "6px" }}
          />
        </Box>
      ))}
    </Box>
  );
};
